import { addDays, endOfToday, startOfToday } from "date-fns";

import {
    BugGroupEntity,
    BugEntity,
    BugStateEntity,
    MapEntity,
    PhaseEntity,
    ProjectEntity,
    RegionEntity,
    VisitEntity,
    WorkPackageEntity,
} from "@archipad-js/archipad-model";
import { LoggerManager } from "@archipad-js/core/logger";
import { comparatorForType, Expression, makeRangeExpression } from "@archipad-js/core/query";

import { WorkPackageService } from "./workpackage.service";

import { MapService } from "./map.service";
import { PhaseService } from "./phase.service";
import { RegionService } from "./region.service";
import { VisitService } from "./visit.service";
import { VisitTypeService } from "./visittype.service";
import { WorkflowService } from "./workflow.service";
import { isNotNullish } from "@archipad-js/core/utils";
import { query } from "@archipad-js/core/orm";

/**
 * Dependency-inversion interface providing permission data required by {@link BugFilterService}.
 *
 * Implemented by the legacy `WorkflowUserManager`.
 */
export interface BugFilterUserProvider {
    bugGroupsAllowingReadNotNull(project: ProjectEntity): ReadonlyArray<BugGroupEntity>;
}

export class BugFilterService {
    private readonly logger = LoggerManager.getLogger("BugFilterService");

    constructor(
        private readonly mapService: MapService,
        private readonly phaseService: PhaseService,
        private readonly regionService: RegionService,
        private readonly visitService: VisitService,
        private readonly visitTypeService: VisitTypeService,
        private readonly workflowService: WorkflowService,
        private readonly workPackageService: WorkPackageService,
        private readonly workflowUserManager: BugFilterUserProvider,
    ) {}

    public listVisits(
        { project, bugGroup: bugGroupContext, map: mapContext }: BugFilterContext,
        { region: regionFilter, map: mapFilter }: BugFilterQuery,
    ): readonly VisitEntity[] {
        const bugGroups =
            bugGroupContext === undefined
                ? this.workflowUserManager.bugGroupsAllowingReadNotNull(project)
                : [bugGroupContext];
        if (bugGroups === undefined) {
            return [];
        }
        const visitTypes = this.visitTypeService.listVisibleVisitTypes({
            where: {
                project,
                bugGroups,
            },
        });
        const regions =
            regionFilter !== undefined
                ? [regionFilter]
                : mapContext?.region !== undefined
                  ? [null, mapContext.region]
                  : mapFilter?.region !== undefined
                    ? [null, mapFilter.region]
                    : undefined;
        const visits = this.visitService.listVisits({
            where: {
                project,
                visitTypes,
                regions,
            },
            orderBy: "date desc, object desc, id desc",
        });
        return visits;
    }

    public listRegions(context: BugFilterContext): readonly (RegionEntity | null)[] {
        const { project } = context;
        const regions = this.regionService.listRegions({ project });
        if (context.visit?.region) {
            return [null, ...regions];
        }
        return regions;
    }

    public listMaps(
        { project, visit: visitContext }: BugFilterContext,
        { visit: visitFilter, region: regionFilter }: BugFilterQuery,
    ): readonly MapEntity[] {
        const region = visitContext?.region ?? visitFilter?.region ?? regionFilter;
        let regions: ReadonlyArray<RegionEntity | null> | undefined;
        if (region === undefined) {
            regions = undefined;
        } else if (visitContext?.region !== undefined) {
            regions = [null, region, ...this.regionService.listDeeplyChildrenRegions(project, region)];
        } else if (visitFilter?.region === null) {
            regions = [null];
        } else if (region === null) {
            regions = [null];
        } else {
            regions = [region, ...this.regionService.listDeeplyChildrenRegions(project, region)];
        }
        const maps = this.mapService.listMaps({
            where: {
                project,
                regions,
            },
            orderBy: "region.id asc, typeOrderHint asc, filename(natural) asc, id asc",
        });
        return maps;
    }

    public listPhases(context: BugFilterContext, filters: BugFilterQuery): readonly PhaseEntity[] {
        const bugGroup = this.resolveBugGroup(context, filters);
        if (bugGroup === undefined) {
            return [];
        }
        const { project } = context;
        const phases = this.phaseService.listPhasesForBugGroup(project, bugGroup);
        return phases;
    }

    public listStates(
        context: BugFilterContext,
        filters: BugFilterQuery,
    ): ReadonlyArray<ComputedState | BugStateEntity> {
        const bugGroup = this.resolveBugGroup(context, filters);
        if (bugGroup === undefined) {
            return [];
        }
        const { project, visit: visitContext } = context;
        const states = this.workflowService.listStates(project, bugGroup);
        const visitStates: ComputedState[] = visitContext
            ? [ComputedState.ActiveAndClosedInVisit, ComputedState.ModifiedInMeeting]
            : [ComputedState.ActiveAndClosedToday, ComputedState.ModifiedToday];

        return [...visitStates, ComputedState.Hot, ComputedState.Late, ...states];
    }

    public listWorkPackages({ project, workPackages }: BugFilterContext): ReadonlyArray<null | WorkPackageEntity> {
        return [null, ...this.workPackageService.listWorkPackages({ where: { project } })].filter((wp) =>
            workPackages.includes(wp),
        );
    }

    public listLocations(context: BugFilterContext): ReadonlyArray<string> {
        const { project } = context;
        const entityContext = project.getContext();

        const queryResult = query<BugEntity>(entityContext, "Bug")
            .predicateWithFormat("map.project == {project} AND location != null AND location != '' AND temp == false", {
                project,
            })
            .orderBy("location(natural) asc")
            .execute();

        const bugs = queryResult.sortedEntities();
        const naturalComparator = comparatorForType("string", "natural");
        const locations = bugs
            .map((bug) => bug.location)
            .filter(isNotNullish)
            .map((location) => location?.trim()?.toLocaleLowerCase())
            .map((location) => location.charAt(0).toUpperCase() + location.slice(1))
            .sort(naturalComparator);
        const uniqueLocations = Array.from(new Set(locations));
        return uniqueLocations;
    }

    /**
     * Returns an {@link Expression} aim to filter a list of {@link BugEntity}.
     *
     * Will filter all observations that match every criteria.
     *
     * @param filters Object containing values used to create an {@link Expression} to filter observations.
     * @returns
     */
    public makeFilterExpression(context: BugFilterContext, filters: BugFilterQuery): Expression {
        const expression = Expression.and(
            this.makeBugGroupFilterExpression(context, filters),
            this.makeMapFilterExpression(context, filters),
            this.makeRegionFilterExpression(context, filters),
            this.makeVisitFilterExpression(context, filters),
            this.makePhasesFilterExpression(filters),
            this.makeStatesFilterExpression(context, filters),
            this.makeLocationFilterExpression(filters),
            this.makeWorkPackageFilterExpression(context, filters),
        );

        this.logger.debug(expression.toString("pretty"), expression.context);
        return expression;
    }

    private makeBugGroupFilterExpression(context: BugFilterContext, filters: BugFilterQuery): Expression {
        const bugGroup = this.resolveBugGroup(context, filters);
        if (bugGroup === undefined) {
            // If there are no bug groups allowing read, filter out all issues
            return Expression.false;
        }

        return Expression.parse("bugGroup == {bugGroup}", { bugGroup });
    }

    private makeMapFilterExpression(
        { map: mapContext }: BugFilterContext,
        { map: mapFilter }: BugFilterQuery,
    ): Expression {
        const map = mapContext ?? mapFilter;
        if (map) {
            return Expression.parse("map == {map}", { map });
        }

        return Expression.true;
    }

    private makeLocationFilterExpression({ location }: BugFilterQuery): Expression {
        if (location === null) {
            return Expression.parse("location == null");
        }

        if (location !== undefined) {
            return Expression.parse("toLowerCase(toTrim(location)) == toLowerCase(toTrim({location}))", { location });
        }

        return Expression.true;
    }

    private makeRegionFilterExpression(
        { visit: visitContext }: BugFilterContext,
        { region: regionFilter, visit: visitFilter }: BugFilterQuery,
    ): Expression {
        const region = visitContext?.region ?? regionFilter;
        const deeplyChilds = region
            ? this.regionService.listDeeplyChildrenRegions(
                  visitContext?.project ?? visitFilter?.project ?? region.project,
                  region,
              )
            : [];
        if (visitContext?.region != null) {
            return Expression.parse("map.region IN {regions}", { regions: [null, region, ...deeplyChilds] });
        }
        if (region !== undefined) {
            return Expression.parse("map.region IN {regions}", { regions: [region, ...deeplyChilds] });
        }

        return Expression.true;
    }

    private makeVisitFilterExpression(
        { visit: visitContext }: BugFilterContext,
        { visit: visitFilter }: BugFilterQuery,
    ): Expression {
        if (visitFilter) {
            return Expression.or(
                // Keep issues opened in that visit
                Expression.parse("visit == {visit}", { visit: visitFilter }),
                // Keep issues closed in that visit
                Expression.parse("closeVisit == {visit}", { visit: visitFilter }),
                // Keep issues updated in that visit
                Expression.parse("stateNoteCache.visit == {visit}", { visit: visitFilter }),
            );
        }

        return Expression.true;
    }

    private makePhasesFilterExpression({ phases }: BugFilterQuery): Expression {
        if (phases && phases.length > 0) {
            return Expression.parse("currentPhase in {phases}", { phases });
        }

        return Expression.true;
    }

    private makeStatesFilterExpression(
        { project, visit: visitContext }: BugFilterContext,
        { states, visit: visitFilter }: BugFilterQuery,
    ): Expression {
        if (!states || states.length === 0) {
            return Expression.true;
        }

        const statesExpressions = [];

        const todayStart = startOfToday();
        const todayEnd = endOfToday();

        if (states.includes(ComputedState.ActiveAndClosedInVisit)) {
            const visit = visitContext ?? visitFilter;
            statesExpressions.push(
                Expression.or(
                    Expression.parse("closeDate == null"),
                    Expression.parse("closeVisit IN {visits}", { visits: [visit] }),
                    Expression.parse("visit IN {visits}", { visits: [visit] }),
                ),
            );
        }

        if (states.includes(ComputedState.ActiveAndClosedToday)) {
            statesExpressions.push(
                Expression.or(
                    Expression.parse("closeDate == null"),
                    makeRangeExpression({
                        fieldName: "closeDate",
                        range: {
                            lower: { including: { todayStart } },
                        },
                    }),
                    makeRangeExpression({
                        fieldName: "closeVisit.date",
                        range: {
                            lower: { including: { todayStart } },
                        },
                    }),
                ),
            );
        }

        if (states.includes(ComputedState.ModifiedInMeeting)) {
            const visit = visitContext ?? visitFilter;
            statesExpressions.push(
                Expression.or(
                    Expression.parse("visit == {visit}", { visit }),
                    Expression.parse("closeVisit == {visit}", { visit }),
                    Expression.parse("stateNoteCache.visit == {visit}", { visit }),
                ),
            );
        }

        if (states.includes(ComputedState.ModifiedToday)) {
            statesExpressions.push(Expression.parse("isTodayModified"));
        }

        const hotDueDateLimit = addDays(todayEnd, project.settingHotBugDuration ?? 0);
        const hotMeetingDateLimit = addDays(
            todayEnd,
            -((project.settingLateBugForVisitDuration ?? 0) - (project.settingHotBugDuration ?? 0)),
        );
        const lateMeetingDateLimit = addDays(todayEnd, -(project.settingLateBugForVisitDuration ?? 0));

        if (states.includes(ComputedState.Hot)) {
            statesExpressions.push(
                Expression.and(
                    Expression.parse("closeVisit == null"),
                    Expression.or(
                        Expression.and(
                            Expression.parse("dueDate != null"),
                            makeRangeExpression({
                                fieldName: "dueDate",
                                range: {
                                    lower: { excluding: { todayEnd } },
                                    upper: { including: { hotDueDateLimit } },
                                },
                            }),
                        ),
                        Expression.and(
                            Expression.parse("dueDate == null"),
                            makeRangeExpression({
                                fieldName: "visit.date",
                                range: {
                                    lower: { excluding: { lateMeetingDateLimit } },
                                    upper: { including: { hotMeetingDateLimit } },
                                },
                            }),
                        ),
                    ),
                ),
            );
        }

        if (states.includes(ComputedState.Late)) {
            statesExpressions.push(
                Expression.and(
                    Expression.parse("closeDate == null"),
                    Expression.or(
                        Expression.and(
                            Expression.parse("dueDate != null"),
                            makeRangeExpression({
                                fieldName: "dueDate",
                                range: {
                                    upper: { excluding: { todayStart } },
                                },
                            }),
                        ),
                        Expression.and(
                            Expression.parse("dueDate == null"),
                            makeRangeExpression({
                                fieldName: "openDate",
                                range: {
                                    upper: { excluding: { lateMeetingDateLimit } },
                                },
                            }),
                        ),
                    ),
                ),
            );
        }

        const bugStateEntities = states.filter(
            (state): state is BugStateEntity => state !== null && state instanceof BugStateEntity,
        );

        if (bugStateEntities.length > 0) {
            statesExpressions.push(
                Expression.parse("stateNoteCache.bugState in {bugStateEntities}", { bugStateEntities }),
            );
        }

        return Expression.or(...statesExpressions);
    }

    private makeWorkPackageFilterExpression(
        { workPackages: workPackagesContext }: BugFilterContext,
        { workPackages: workPackageFilter }: BugFilterQuery,
    ): Expression {
        const visibilityExpression = Expression.parse("workPackage IN {workPackages}", {
            workPackages: workPackagesContext,
        });

        const filterExpression =
            workPackageFilter === undefined
                ? Expression.true
                : Expression.parse("workPackage IN {workPackages}", { workPackages: workPackageFilter });

        return Expression.and(visibilityExpression, filterExpression);
    }

    private resolveBugGroup(
        { project, bugGroup: bugGroupContext, visit: visitContext }: BugFilterContext,
        { visit: visitFilter }: BugFilterQuery,
    ): BugGroupEntity | null | undefined {
        if (bugGroupContext === undefined) {
            return undefined;
        }

        const bugGroupsAllowingRead = this.workflowUserManager.bugGroupsAllowingReadNotNull(project);
        if (!bugGroupsAllowingRead?.length) {
            return undefined; // No bug groups allowing read
        }

        const visit = visitContext ?? visitFilter;
        if (visit) {
            return bugGroupsAllowingRead.find((b) => b === visit.visitType!.bugGroup);
        }

        return bugGroupsAllowingRead.find((b) => b === bugGroupContext);
    }
}

export interface BugFilterContext {
    project: ProjectEntity;
    bugGroup: BugGroupEntity | null;
    workPackages: ReadonlyArray<null | WorkPackageEntity>;
    map?: MapEntity;
    visit?: VisitEntity;
}

export interface BugFilterQuery {
    visit?: VisitEntity | undefined;
    region?: RegionEntity | null | undefined;
    map?: MapEntity | undefined;
    phases?: PhaseEntity[] | undefined;
    states?: Array<ComputedState | BugStateEntity | null> | undefined;
    workPackages?: Array<WorkPackageEntity | null> | undefined;
    location?: string | null | undefined;
}

export enum ComputedState {
    ActiveAndClosedInVisit,
    ActiveAndClosedToday,
    ModifiedInMeeting,
    ModifiedToday,
    Hot,
    Late,
}
